import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faClockRotateLeft, faDollarSign, faGear, faWallet } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, Timestamp } from 'firebase/firestore';
import { getFirestore } from '@firebase/firestore';
import { app } from '../firebase.js';
import { logAdminActivity } from '../utils/activityLogger';

const CURRENCY_SETTINGS_COLLECTION = 'Settings';
const CURRENCY_SETTINGS_DOC = 'currency';
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';
const EXCHANGE_RATE_HISTORY_COLLECTION = 'ExchangeRateHistory';
const ADMISSION_FEE_HISTORY_COLLECTION = 'AdmissionFeeSettingsHistory';
const DEFAULT_USD_TO_KES_RATE = 130;
const DEFAULT_ADMISSION_FEE_AMOUNT = 2000;

const formatNumber = (value, maximumFractionDigits = 4) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';

  return number.toLocaleString('en-KE', {
    maximumFractionDigits
  });
};

const formatDateTime = (value) => {
  if (!value) return 'Not updated yet';

  const dateObj = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return 'Not updated yet';

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

function SettingsComp() {
  const db = getFirestore(app);
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [admissionFeeSettings, setAdmissionFeeSettings] = useState(null);
  const [usdToKesRate, setUsdToKesRate] = useState('');
  const [admissionFeeAmount, setAdmissionFeeAmount] = useState('');
  const [rateNote, setRateNote] = useState('');
  const [admissionFeeNote, setAdmissionFeeNote] = useState('');
  const [history, setHistory] = useState([]);
  const [savingAdmissionFee, setSavingAdmissionFee] = useState(false);

  const numericUsdToKesRate = useMemo(() => Number(usdToKesRate), [usdToKesRate]);
  const numericAdmissionFeeAmount = useMemo(() => Number(admissionFeeAmount), [admissionFeeAmount]);
  const validRate = Number.isFinite(numericUsdToKesRate) && numericUsdToKesRate > 0;
  const validAdmissionFee = Number.isFinite(numericAdmissionFeeAmount) && numericAdmissionFeeAmount > 0;
  const kesToUsdRate = validRate ? 1 / numericUsdToKesRate : 0;
  const sampleKes = 100000;
  const sampleUsd = 1000;

  const fetchSettings = useCallback(async () => {
    setLoading(true);

    try {
      const [settingsSnap, admissionFeeSnap, historySnap] = await Promise.all([
        getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, CURRENCY_SETTINGS_DOC)),
        getDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, ADMISSION_FEE_SETTINGS_DOC)),
        getDocs(query(
          collection(db, EXCHANGE_RATE_HISTORY_COLLECTION),
          orderBy('createdAt', 'desc'),
          limit(8)
        ))
      ]);

      const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
      const admissionFeeData = admissionFeeSnap.exists() ? admissionFeeSnap.data() : null;
      setSettings(settingsData);
      setAdmissionFeeSettings(admissionFeeData);
      setUsdToKesRate(String(settingsData?.usdToKesRate || DEFAULT_USD_TO_KES_RATE));
      setAdmissionFeeAmount(String(admissionFeeData?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT));

      const historyItems = [];
      historySnap.forEach((historyDoc) => {
        historyItems.push({ id: historyDoc.id, ...historyDoc.data() });
      });
      setHistory(historyItems);
    } catch (error) {
      console.error('Error loading settings:', error);
      swal('Error', 'Failed to load settings. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveCurrencySettings = useCallback(async () => {
    if (!validRate) {
      swal('Invalid rate', 'Enter a valid exchange rate greater than zero.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please log in again before updating settings.', 'error');
      return;
    }

    setSaving(true);

    const now = Timestamp.now();
    const payload = {
      baseCurrency: 'KES',
      quoteCurrency: 'USD',
      usdToKesRate: numericUsdToKesRate,
      kesToUsdRate: Number(kesToUsdRate.toFixed(8)),
      updatedAt: now,
      updatedBy: currentUser.uid,
      updatedByEmail: currentUser.email || '',
      note: rateNote.trim()
    };

    try {
      await Promise.all([
        setDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, CURRENCY_SETTINGS_DOC), payload, { merge: true }),
        addDoc(collection(db, EXCHANGE_RATE_HISTORY_COLLECTION), {
          ...payload,
          createdAt: now
        })
      ]);

      setSettings(payload);
      setRateNote('');
      await fetchSettings();

      logAdminActivity({
        action: 'Update Settings',
        details: `Updated USD to KES exchange rate to ${numericUsdToKesRate}`,
        meta: {
          page: 'Settings',
          setting: 'currency_exchange_rate',
          usdToKesRate: numericUsdToKesRate,
          kesToUsdRate: Number(kesToUsdRate.toFixed(8))
        }
      });

      swal('Settings updated', 'The exchange rate has been saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving currency settings:', error);
      swal('Error', 'Failed to save the exchange rate. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }, [auth, db, fetchSettings, kesToUsdRate, numericUsdToKesRate, rateNote, validRate]);

  const saveAdmissionFeeSettings = useCallback(async () => {
    if (!validAdmissionFee) {
      swal('Invalid fee', 'Enter a valid admission fee greater than zero.', 'warning');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please log in again before updating settings.', 'error');
      return;
    }

    setSavingAdmissionFee(true);

    const now = Timestamp.now();
    const payload = {
      amountKes: numericAdmissionFeeAmount,
      currency: 'KES',
      updatedAt: now,
      updatedBy: currentUser.uid,
      updatedByEmail: currentUser.email || '',
      note: admissionFeeNote.trim()
    };

    try {
      await Promise.all([
        setDoc(doc(db, CURRENCY_SETTINGS_COLLECTION, ADMISSION_FEE_SETTINGS_DOC), payload, { merge: true }),
        addDoc(collection(db, ADMISSION_FEE_HISTORY_COLLECTION), {
          ...payload,
          createdAt: now
        })
      ]);

      setAdmissionFeeSettings(payload);
      setAdmissionFeeNote('');

      logAdminActivity({
        action: 'Update Settings',
        details: `Updated admission fee to KES ${numericAdmissionFeeAmount}`,
        meta: {
          page: 'Settings',
          setting: 'admission_fee_amount',
          amountKes: numericAdmissionFeeAmount
        }
      });

      swal('Settings updated', 'The admission fee has been saved successfully.', 'success');
    } catch (error) {
      console.error('Error saving admission fee settings:', error);
      swal('Error', 'Failed to save the admission fee. Please try again.', 'error');
    } finally {
      setSavingAdmissionFee(false);
    }
  }, [admissionFeeNote, auth, db, numericAdmissionFeeAmount, validAdmissionFee]);

  return (
    <div className="clientsComp applicationsPage settingsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">System Settings</p>
          <h4>Settings</h4>
          <p className="appsSubtext">Control operational values that the admin app can reuse across finance, admissions, and reporting.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>USD to KES</span>
            <strong>KES {formatNumber(settings?.usdToKesRate || numericUsdToKesRate, 2)}</strong>
          </div>
          <div className="appsStatChip">
            <span>KES to USD</span>
            <strong>USD {formatNumber(settings?.kesToUsdRate || kesToUsdRate, 8)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Updated</span>
            <strong>{formatDateTime(settings?.updatedAt)}</strong>
          </div>
          <div className="appsStatChip">
            <span>Admission Fee</span>
            <strong>KES {formatNumber(admissionFeeSettings?.amountKes || numericAdmissionFeeAmount, 0)}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard settingsToolbar">
        <div>
          <p className="settingsToolbarLabel">Finance controls</p>
          <span>Exchange rate and admission fee values shared across Admin and Students Portal.</span>
        </div>
        <Button variant="outline-dark" className="settingsRefreshBtn" onClick={fetchSettings} disabled={loading || saving || savingAdmissionFee}>
          <FontAwesomeIcon icon={faArrowsRotate} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="settingsLoadingState">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading settings...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <div className="settingsGrid">
            <div className="settingsCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble">
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                <div>
                  <h5>Currency Exchange Rate</h5>
                  <p>Update the rate used when converting between KES and USD.</p>
                </div>
              </div>

              <div className="settingsFormGrid">
                <Form.Group>
                  <Form.Label>USD to KES rate</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="0.01"
                    value={usdToKesRate}
                    onChange={(event) => setUsdToKesRate(event.target.value)}
                    placeholder="e.g. 130"
                  />
                  <Form.Text className="text-muted">
                    Enter how many Kenyan shillings equal 1 US dollar.
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Optional note</Form.Label>
                  <Form.Control
                    type="text"
                    value={rateNote}
                    onChange={(event) => setRateNote(event.target.value)}
                    placeholder="e.g. Updated from bank rate"
                  />
                  <Form.Text className="text-muted">
                    This note is saved in the exchange-rate history.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="settingsPreviewCard">
                <p>Live preview</p>
                <div>
                  <span>KES {formatNumber(sampleKes, 0)} = USD {validRate ? formatNumber(sampleKes / numericUsdToKesRate, 2) : '0'}</span>
                  <span>USD {formatNumber(sampleUsd, 0)} = KES {validRate ? formatNumber(sampleUsd * numericUsdToKesRate, 0) : '0'}</span>
                </div>
              </div>

              <div className="settingsActions">
                <Button variant="dark" onClick={saveCurrencySettings} disabled={saving}>
                  {saving ? 'Saving...' : 'Save exchange rate'}
                </Button>
              </div>
            </div>

            <div className="settingsCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div>
                  <h5>Admission Fee</h5>
                  <p>Set the admission fee used by Admin and the Students Portal.</p>
                </div>
              </div>

              <div className="settingsFormGrid">
                <Form.Group>
                  <Form.Label>Admission fee amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={admissionFeeAmount}
                    onChange={(event) => setAdmissionFeeAmount(event.target.value)}
                    placeholder="e.g. 2000"
                  />
                  <Form.Text className="text-muted">
                    Amount is saved in KES and reused when recording or paying admission fees.
                  </Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Optional note</Form.Label>
                  <Form.Control
                    type="text"
                    value={admissionFeeNote}
                    onChange={(event) => setAdmissionFeeNote(event.target.value)}
                    placeholder="e.g. Updated for May intake"
                  />
                  <Form.Text className="text-muted">
                    This note is saved in the admission-fee settings history.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="settingsPreviewCard">
                <p>Current shared value</p>
                <div>
                  <span>KES {formatNumber(validAdmissionFee ? numericAdmissionFeeAmount : DEFAULT_ADMISSION_FEE_AMOUNT, 0)}</span>
                  <span>Last updated: {formatDateTime(admissionFeeSettings?.updatedAt)}</span>
                </div>
              </div>

              <div className="settingsActions">
                <Button variant="dark" onClick={saveAdmissionFeeSettings} disabled={savingAdmissionFee}>
                  {savingAdmissionFee ? 'Saving...' : 'Save admission fee'}
                </Button>
              </div>
            </div>

            <div className="settingsCard settingsHistoryCard">
              <div className="settingsCardHeader">
                <div className="settingsIconBubble settingsIconBubbleMuted">
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                </div>
                <div>
                  <h5>Recent Updates</h5>
                  <p>Last exchange-rate changes for quick audit visibility.</p>
                </div>
              </div>

              {history.length === 0 ? (
                <div className="settingsEmptyState">
                  No exchange-rate updates recorded yet.
                </div>
              ) : (
                <div className="settingsHistoryList">
                  {history.map((item) => (
                    <div className="settingsHistoryItem" key={item.id}>
                      <div>
                        <strong>1 USD = KES {formatNumber(item.usdToKesRate, 2)}</strong>
                        <span>{item.updatedByEmail || 'Unknown admin'} • {formatDateTime(item.createdAt || item.updatedAt)}</span>
                        {item.note && <p>{item.note}</p>}
                      </div>
                      <FontAwesomeIcon icon={faGear} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SettingsComp;
